'use client';

import { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import EmojiRating from './EmojiRating';
import ReviewImageUpload from './ReviewImageUpload';
import type { PendingImage } from './ReviewImageUpload';
import { createReviewAction, updateReviewAction } from '@/app/actions/reviews';
import { createClient } from '@/lib/supabase/client';
import type { ProductReview } from '@/types';

interface ReviewFormProps {
  productId: string;
  partnerId: string;
  reservationId: string;
  partnerName?: string;
  existingReview?: ProductReview | null;
  onSuccess?: (review?: ProductReview) => void;
  onCancel?: () => void;
}

/**
 * 이미지를 Supabase Storage에 직접 업로드.
 *
 * - API route 경유 없이 클라이언트 → Storage 직행 (네트워크 왕복 1번으로 단축).
 * - 경로는 `{userId}/{productId}/{timestamp}_{random}_{sanitizedName}` 규칙.
 *   RLS 정책이 경로 첫 폴더 = auth.uid() 일치를 강제하므로 다른 사용자 폴더엔
 *   업로드 불가. 파일 크기/MIME은 버킷 레벨에서 Supabase가 자동 검증.
 * - 여러 파일은 Promise.all로 병렬 업로드.
 */
async function uploadImages(files: File[], productId: string): Promise<string[]> {
  if (files.length === 0) return [];

  const supabase = createClient();
  const { data, error: authError } = await supabase.auth.getClaims();
  const userId = data?.claims?.sub as string | undefined;

  if (authError || !userId) {
    throw new Error('errorAuth');
  }
  const user = { id: userId };

  const results = await Promise.all(
    files.map(async (file) => {
      const sanitizedName = file.name
        .replace(/[^a-zA-Z0-9.-]/g, '_')
        .substring(0, 50);
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 8);
      const fileName = `${user.id}/${productId}/${timestamp}_${randomStr}_${sanitizedName}`;

      const { error: uploadError } = await supabase.storage
        .from('review-images')
        // 파일은 ReviewImageUpload 에서 compressReviewImage 로 WebP(1600px, q0.8)
        // 변환된 상태. 경로가 매번 고유하므로 1년 캐시.
        .upload(fileName, file, {
          cacheControl: '31536000',
          contentType: file.type || undefined,
          upsert: false,
        });

      if (uploadError) {
        console.error('[Review] Storage upload error:', uploadError);
        throw new Error('uploadError');
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from('review-images').getPublicUrl(fileName);

      return publicUrl;
    }),
  );

  return results;
}

export default function ReviewForm({
  productId,
  partnerId,
  reservationId,
  partnerName,
  existingReview,
  onSuccess,
  onCancel,
}: ReviewFormProps) {
  const locale = useLocale();
  const t = useTranslations('reviews');
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [comment, setComment] = useState(existingReview?.comment || '');
  // 기존 업로드된 이미지 URL (수정 모드용)
  const [existingImages, setExistingImages] = useState<string[]>(
    existingReview?.images || []
  );
  // 새로 선택한 파일 (아직 업로드 안 됨)
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!existingReview;

  const handleRatingChange = useCallback((newRating: number) => {
    setRating(newRating);
  }, []);

  const handleAddFiles = useCallback((files: File[]) => {
    const newPending = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPendingImages((prev) => [...prev, ...newPending]);
  }, []);

  const handleRemoveExisting = useCallback((index: number) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemovePending = useCallback((index: number) => {
    setPendingImages((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rating === 0) {
      setError(t('errorNoRating'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // 새 이미지가 있으면 제출 시 업로드
      let uploadedUrls: string[] = [];
      if (pendingImages.length > 0) {
        const files = pendingImages.map((p) => p.file);
        uploadedUrls = await uploadImages(files, productId);
      }

      const allImages = [...existingImages, ...uploadedUrls];

      if (isEditing && existingReview) {
        const result = await updateReviewAction(existingReview.id, {
          rating,
          comment: comment || undefined,
          images: allImages,
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        onSuccess?.();
      } else {
        const result = await createReviewAction({
          productId,
          partnerId,
          reservationId,
          rating,
          comment: comment || undefined,
          images: allImages,
          sourceLang: locale,
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        onSuccess?.(result.review);
      }

      // 성공: preview URL 정리. isSubmitting은 풀지 않음 — 부모의 router.push()가
      // 완료되기 전에 버튼이 다시 활성화돼 더블 클릭이 들어오는 것을 방지.
      pendingImages.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    } catch (err) {
      const errorKey = err instanceof Error ? err.message : 'errorCreate';
      const translatedError = t(errorKey);
      setError(translatedError !== errorKey ? translatedError : t('errorCreate'));
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* 섹션 1: 이모지 선호도 */}
      <div className="text-center">
        <h3 className="text-base font-semibold text-gray-900">
          {t('ratingQuestion', { partnerName: partnerName || '' })}
        </h3>
        <p className="text-sm text-gray-500 mt-1 mb-5">
          {t('ratingQuestionSubtitle')}
        </p>
        <EmojiRating rating={rating} onChange={handleRatingChange} size="lg" />
      </div>

      {/* 섹션 2: 코멘트 + 이미지 */}
      <div>
        <h3 className="text-base font-semibold text-gray-900">
          {t('commentSectionTitle')}
        </h3>
        <p className="text-sm text-gray-500 mt-1 mb-3">
          {t('commentSectionSubtitle')}
        </p>
        <div className="relative">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('commentPlaceholderNew')}
            rows={8}
            maxLength={1000}
            className="resize-none rounded-xl border-gray-200 min-h-48"
          />
          <p className="absolute bottom-3 right-3 text-xs text-gray-400">
            {comment.length}/1000
          </p>
        </div>
        <div className="mt-3">
          <ReviewImageUpload
            existingImages={existingImages}
            pendingImages={pendingImages}
            maxImages={5}
            onAddFiles={handleAddFiles}
            onRemoveExisting={handleRemoveExisting}
            onRemovePending={handleRemovePending}
            disabled={isSubmitting}
          />
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* 제출 버튼 */}
      <Button
        type="submit"
        disabled={isSubmitting || rating === 0}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-12 text-base font-medium disabled:bg-gray-200 disabled:text-gray-400"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('submitting')}
          </>
        ) : isEditing ? (
          t('update')
        ) : (
          t('submitReview')
        )}
      </Button>
    </form>
  );
}
